import { Controller } from "@hotwired/stimulus"
import { renderStreamMessage } from "@hotwired/turbo"

// Página del buscador por tienda (Fase 3): recibe el término del campo nativo (bridge--search:queried) o del
// input web, espera, pide los resultados como Turbo Stream y los morfea en #search-results, con la URL al día.
// Doc: storefront_search.md → Fase 3
export default class extends Controller {
  static targets = ["input", "clear"]
  static values = {
    url: String, term: String, delay: { type: Number, default: 250 }, minLength: { type: Number, default: 2 },
    retryDelay: { type: Number, default: 1000 }
  }

  // Lo que la página trae pintado (?q= en una carga directa, o el snapshot que Turbo restaura al volver de una
  // ficha: load anota en el value el término de los resultados que pintó) no se vuelve a pedir. Si el input web
  // trae otra cosa, la consulta quedó pendiente al abrir la ficha: se reanuda. Doc: storefront_search.md → Fase 5
  connect() {
    this.loadedTerm = this.effectiveTerm(this.termValue)
    if (this.hasInputTarget && this.effectiveTerm(this.inputTarget.value) !== this.loadedTerm) this.update(this.inputTarget.value)
  }

  // Campo nativo de la app: { query } en el detail del evento
  search(event) {
    this.update(event.detail.query)
  }

  // Input web
  type(event) {
    this.update(event.target.value)
  }

  clear() {
    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.update("")
  }

  // Enter en el teclado móvil esconde el teclado, como el campo nativo; no hay formulario que enviar
  blur(event) {
    event.preventDefault()
    event.target.blur()
  }

  // Única entrada del término, venga de donde venga: espera delay ms y carga los resultados
  update(query) {
    if (this.hasClearTarget) this.clearTarget.hidden = this.inputTarget.value === ""
    clearTimeout(this.timeout)
    this.cancelRetry()
    this.timeout = setTimeout(() => this.load(this.effectiveTerm(query)), this.delayValue)
  }

  // Un término corto vuelve al estado vacío (el servidor lo renderiza sin q); el mismo término no recarga.
  // Una sola carga en vuelo: la letra nueva aborta la anterior. El stream llega con method="morph", así los
  // tiles que siguen en pantalla conservan sus nodos y las fotos no parpadean. La URL y el value recién cambian
  // con los resultados pintados (gate: respuesta ok): un snapshot tomado con la consulta en vuelo vuelve con lo
  // viejo y repetir el término sí consulta. Doc: storefront_search.md → Fase 4
  async load(term) {
    if (term === this.loadedTerm) return
    this.loadedTerm = term
    this.cancelRetry()
    const url = term ? `${this.urlValue}?q=${encodeURIComponent(term)}` : this.urlValue

    this.request?.abort()
    this.request = new AbortController()
    try {
      const response = await fetch(url, { headers: { Accept: "text/vnd.turbo-stream.html" }, signal: this.request.signal })
      if (!response.ok) throw new Error(`search ${response.status}`)
      renderStreamMessage(await response.text())
      history.replaceState(history.state, "", url)
      this.termValue = term
      this.retriedTerm = null
    } catch (error) {
      if (error.name === "AbortError") return
      this.loadedTerm = null
      this.retryLater(term)
      console.warn(`store-search: ${error.message}`)
    }
  }

  // Un fallo de red o del servidor no se muestra: siguen los resultados anteriores y se reintenta una sola vez
  // pasado retryDelay; después, al volver a estar en línea (resume) o con la próxima letra. Doc: storefront_search.md → Fase 5
  retryLater(term) {
    this.failedTerm = term
    if (this.retriedTerm === term) return
    this.retriedTerm = term
    this.retryTimeout = setTimeout(() => this.load(term), this.retryDelayValue)
  }

  // online@window: vuelve a pedir el término que falló
  resume() {
    if (this.failedTerm != null) return this.load(this.failedTerm)
  }

  cancelRetry() {
    clearTimeout(this.retryTimeout)
    this.failedTerm = null
  }

  effectiveTerm(query) {
    const term = (query ?? "").trim()
    return term.length >= this.minLengthValue ? term : ""
  }

  disconnect() {
    clearTimeout(this.timeout)
    clearTimeout(this.retryTimeout)
    this.request?.abort()
  }
}
